/**
 * `pow(Double, Double)` as Darwin's libm computes it: correctly rounded (to within the
 * recorded vectors' 0.1%). V8's Math.pow is off by an ulp for ~7% of the growth factors the
 * engine uses, which can flip a half-cent after rounding and then compound through
 * cumulative series, so pow is evaluated as exp(y·ln x) in double-double arithmetic
 * (~100 bits) and rounded once.
 */

type DD = readonly [hi: number, lo: number];

const SPLITTER = 134217729; // 2^27 + 1

function twoSum(a: number, b: number): DD {
  const s = a + b;
  const bb = s - a;
  return [s, a - (s - bb) + (b - bb)];
}

function quickTwoSum(a: number, b: number): DD {
  const s = a + b;
  return [s, b - (s - a)];
}

function split(a: number): DD {
  const c = SPLITTER * a;
  const hi = c - (c - a);
  return [hi, a - hi];
}

function twoProd(a: number, b: number): DD {
  const p = a * b;
  const [ah, al] = split(a);
  const [bh, bl] = split(b);
  return [p, ah * bh - p + ah * bl + al * bh + al * bl];
}

function ddAdd(a: DD, b: DD): DD {
  const [s, e] = twoSum(a[0], b[0]);
  const [t, f] = twoSum(a[1], b[1]);
  const [s2, e2] = quickTwoSum(s, e + t);
  return quickTwoSum(s2, e2 + f);
}

function ddMul(a: DD, b: DD): DD {
  const [p, e] = twoProd(a[0], b[0]);
  return quickTwoSum(p, e + (a[0] * b[1] + a[1] * b[0]));
}

function ddDiv(a: DD, b: DD): DD {
  const q1 = a[0] / b[0];
  const r1 = ddAdd(a, ddMul(b, [-q1, 0]));
  const q2 = r1[0] / b[0];
  const r2 = ddAdd(r1, ddMul(b, [-q2, 0]));
  const q3 = r2[0] / b[0];
  return ddAdd(quickTwoSum(q1, q2), [q3, 0]);
}

const LN2: DD = [0.6931471805599453, 2.3190468138462996e-17];
const ONE: DD = [1, 0];

/** 1/(2n+1) for the atanh series, n = 0 … 24. */
const ODD_RECIPROCALS: readonly DD[] = Array.from({ length: 25 }, (_, n) => ddDiv(ONE, [2 * n + 1, 0]));
/** 1/n! for n = 0 … 12. */
const INVERSE_FACTORIALS: readonly DD[] = Array.from({ length: 13 }, (_, n) => n).reduce<DD[]>(
  (table, n) => [...table, n === 0 ? ONE : ddDiv(table[n - 1]!, [n, 0])],
  [],
);

/** Split a positive finite x into m·2^k with m in [√½, √2). */
function frexp(x: number): [m: number, k: number] {
  const view = new DataView(new ArrayBuffer(8));
  view.setFloat64(0, x);
  const biased = (view.getUint16(0) >> 4) & 0x7ff;
  if (biased === 0) {
    const [m, k] = frexp(x * 2 ** 54);
    return [m, k - 54];
  }
  let k = biased - 1023;
  let m = x / 2 ** k;
  if (m > Math.SQRT2) {
    m /= 2;
    k += 1;
  }
  return [m, k];
}

/** ln x = k·ln2 + 2·atanh((m − 1)/(m + 1)). */
function ddLog(x: number): DD {
  const [m, k] = frexp(x);
  const s = ddDiv([m - 1, 0], twoSum(m, 1));
  const s2 = ddMul(s, s);
  let acc = ODD_RECIPROCALS[ODD_RECIPROCALS.length - 1]!;
  for (let n = ODD_RECIPROCALS.length - 2; n >= 0; n--) acc = ddAdd(ddMul(acc, s2), ODD_RECIPROCALS[n]!);
  const lnM = ddMul([2 * s[0], 2 * s[1]], acc);
  return ddAdd(ddMul(LN2, [k, 0]), lnM);
}

const SQUARINGS = 10;

/** e^t = 2^k · (1 + expm1(r / 2^10))^(2^10) with t = k·ln2 + r. */
function ddExp(t: DD): [DD, number] {
  const k = Math.round(t[0] / LN2[0]);
  const r = ddAdd(t, ddMul(LN2, [-k, 0]));
  const scaled: DD = [r[0] / 2 ** SQUARINGS, r[1] / 2 ** SQUARINGS];
  let p: DD = INVERSE_FACTORIALS[INVERSE_FACTORIALS.length - 1]!;
  for (let n = INVERSE_FACTORIALS.length - 2; n >= 1; n--) p = ddAdd(ddMul(p, scaled), INVERSE_FACTORIALS[n]!);
  p = ddMul(p, scaled);
  for (let i = 0; i < SQUARINGS; i++) p = ddAdd([2 * p[0], 2 * p[1]], ddMul(p, p));
  return [ddAdd(ONE, p), k];
}

export function powDouble(x: number, y: number): number {
  if (!(x > 0) || !Number.isFinite(x) || !Number.isFinite(y) || y === 0 || x === 1) return Math.pow(x, y);
  const [hi, lo] = ddLog(x);
  const [product, err] = twoProd(y, hi);
  const t = quickTwoSum(product, err + y * lo);
  if (Math.abs(t[0]) > 700) return Math.pow(x, y);
  const [e, k] = ddExp(t);
  return (e[0] + e[1]) * 2 ** k;
}
